import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowLeft, CheckSquare, Folder, GitBranch } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Conditionally renders the appropriate workspace header based on type
export default function WorkspaceTopbar({
  type = "workspace", // "workspaces", "workspace", "workflow", "task"
  workspace,
  title,
  subtitle,
  onBackClick,
  actionButton,
  actionButtons,
  className,
  backButtonOnly = false, // New prop to show only back button
  ...props
}) {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Track window size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Default back click handler
  const handleBackClick = () => {
    console.log("WorkspaceTopbar handleBackClick called with type", type);
    if (onBackClick) {
      onBackClick();
    } else {
      // Default navigation based on type
      if (type === "workspace" && workspace?.id) {
        // Navigate to projects list from a workspace
        navigate("/projects");
      } else if (type === "workflow" && workspace?.id) {
        // Navigate to workspace from a workflow
        navigate(`/projects/${workspace.id}`);
      } else if (type === "task" && workspace?.id) {
        // Navigate back to workspace from a task
        navigate(`/projects/${workspace.id}`);
      } else {
        // Default fallback
        navigate("/projects");
      }
    }
  };

  // Render appropriate icon based on type
  const renderIcon = () => {
    switch (type) {
      case "workspaces":
        return <Folder className="h-5 w-5 text-purple-500 relative top-0" />;
      case "workspace":
        return <Folder className="h-5 w-5 text-purple-500 relative top-0" />;
      case "workflow":
        return <GitBranch className="h-5 w-5 text-cyan-500 relative top-0" />;
      case "task":
        return (
          <CheckSquare className="h-5 w-5 text-emerald-500 relative top-0" />
        );
      default:
        return <Folder className="h-5 w-5 relative top-0" />;
    }
  };

  // Get default title based on type
  const getDefaultTitle = () => {
    switch (type) {
      case "workspaces":
        return "Projects";
      case "workspace":
        return workspace?.name || "Project";
      case "workflow":
        return "Workflows";
      case "task":
        return "Tasks";
      default:
        return "";
    }
  };

  // Get default subtitle based on type
  const getDefaultSubtitle = () => {
    switch (type) {
      case "workspaces":
        return "Manage your projects";
      case "workspace":
        return workspace?.description || "Project details";
      case "workflow":
        return workspace?.name || "";
      case "task":
        return workspace?.name || "";
      default:
        return "";
    }
  };

  // If backButtonOnly is true, render just the back button
  if (backButtonOnly) {
    return (
      <div
        className={cn(
          "bg-transparent px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between w-full z-50",
          className
        )}
        {...props}
      >
        <div className="flex items-center min-w-0 flex-1 pl-0 -ml-4">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "flex h-8 w-8 items-center justify-center",
              "rounded-md",
              "bg-transparent",
              "border-2 border-purple-500/[0.15]",
              "hover:bg-transparent",
              "hover:border-purple-500/50",
              "transition-all duration-200",
              "focus:outline-none",
              "focus:ring-2",
              "focus:ring-purple-400/30",
              "flex-shrink-0",
              "focus:border-purple-400/50",
              "group",
              "text-purple-500",
              "active:scale-95",
              "touch-manipulation",
              "-ml-1",
              isMobile && "mt-1.5"
            )}
            onClick={handleBackClick}
          >
            <ArrowLeft className="h-5 w-5 text-purple-500" />
          </Button>
        </div>

        {actionButtons && (
          <div className="flex items-center gap-3 flex-shrink-0">
            {actionButtons}
          </div>
        )}
      </div>
    );
  }

  // Render different header styles based on type
  if (type === "task") {
    // Updated task header style to match other workspace pages
    return (
      <div
        className={cn(
          "bg-transparent px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between w-full z-50",
          !backButtonOnly && "border-b border-gray-200 dark:border-zinc-800",
          className
        )}
        {...props}
      >
        <div className="flex items-center min-w-0 flex-1 pl-0 -ml-4">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "flex h-8 w-8 items-center justify-center",
              "rounded-md",
              "bg-transparent",
              "border-2 border-purple-500/[0.15]",
              "hover:bg-transparent",
              "hover:border-purple-500/50",
              "transition-all duration-200",
              "focus:outline-none",
              "focus:ring-2",
              "focus:ring-purple-400/30",
              "flex-shrink-0",
              "focus:border-purple-400/50",
              "group",
              "text-purple-500",
              "active:scale-95",
              "touch-manipulation",
              "-ml-1",
              isMobile && "mt-1.5"
            )}
            onClick={handleBackClick}
          >
            <ArrowLeft className="h-5 w-5 text-purple-500" />
          </Button>
          <div className="min-w-0 flex-1 pl-4 pr-4">
            <h1 className="font-semibold text-lg text-gray-900 dark:text-white flex items-center gap-2 truncate">
              {renderIcon()}
              <div className="flex items-baseline">
                <span className="truncate">{title || getDefaultTitle()}</span>
                {(subtitle || getDefaultSubtitle()) && (
                  <span className="text-sm font-normal text-gray-500 dark:text-zinc-400 flex items-baseline">
                    <span className="mx-1.5">|</span>
                    <span>{subtitle || getDefaultSubtitle()}</span>
                  </span>
                )}
              </div>
            </h1>
          </div>
        </div>

        {actionButtons && (
          <div className="flex items-center gap-3 flex-shrink-0">
            {actionButtons}
          </div>
        )}
      </div>
    );
  }

  // Default header style (workflows, workspaces, workspace)
  return (
    <div
      className={cn(
        "bg-transparent px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between w-full z-50",
        // Only add border for non-workspace types
        type !== "workspace" &&
          type !== "workflow" &&
          "border-b border-gray-200 dark:border-zinc-800",
        // Simpler styling for workflow type
        type === "workflow" && "py-1 h-10",
        className
      )}
      {...props}
    >
      <div className="flex items-center min-w-0 flex-1 pl-0 -ml-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBackClick}
          className={cn(
            "flex h-8 w-8 items-center justify-center",
            "rounded-md",
            "bg-transparent",
            "border-2 border-purple-500/[0.15]",
            "hover:bg-transparent",
            "hover:border-purple-500/50",
            "transition-all duration-200",
            "focus:outline-none",
            "focus:ring-2",
            "focus:ring-purple-400/30",
            "flex-shrink-0",
            "focus:border-purple-400/50",
            "group",
            "text-purple-500",
            "active:scale-95",
            "touch-manipulation",
            "-ml-1",
            isMobile && "mt-1.5"
          )}
        >
          <ArrowLeft className="h-5 w-5 text-purple-500" />
        </Button>
        <div className="min-w-0 flex-1 pl-4 pr-4">
          <h1
            className={cn(
              "font-semibold text-lg text-gray-900 dark:text-white flex items-center gap-2 truncate"
            )}
          >
            {/* {renderIcon()} */}
            <div className="flex items-baseline">
              <span className="truncate">{title || getDefaultTitle()}</span>
              {/* {(subtitle || getDefaultSubtitle()) && (
                <span className="text-sm font-normal text-gray-500 dark:text-zinc-400 flex items-baseline">
                  <span className="mx-1.5">|</span>
                  <span>{subtitle || getDefaultSubtitle()}</span>
                </span>
              )} */}
            </div>
          </h1>
        </div>
      </div>

      <div className="flex-shrink-0 flex justify-end">
        {actionButton && <div className="flex-shrink-0">{actionButton}</div>}
        {actionButtons && !actionButton && (
          <div className="flex items-center gap-3 flex-shrink-0">
            {actionButtons}
          </div>
        )}
      </div>
    </div>
  );
}
